import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getApplicationNotes,
  saveApplicantVerifiedIncome,
} from "../../../../services/applicationService";
import { formatDateTime } from "../../../../utils/applicationFormatters";
import { showSuccess, showError } from "@/lib/utils/notifications";
import { useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import { VERIFIED_INCOME_ROLES } from "../../../../constants";
import {
  resolveIncomeTabVerifiedIncome,
  showVerifiedIncomeSummary as showVerifiedIncomeSummaryHelper,
} from "../incomeEmploymentHelpers";

export function useVerifiedIncome(options: {
  applicantId: string;
  applicationId?: string | number;
  applicationData: Record<string, unknown>;
  embeddedNotes?: unknown;
  onRefresh?: () => void | Promise<void>;
}) {
  const { applicantId, applicationId, applicationData, embeddedNotes, onRefresh } =
    options;
  const user = useAppSelector(selectUser);
  const currentUserRole = user?.current_role?.role_name || "";
  const canEditVerifiedIncome = VERIFIED_INCOME_ROLES.includes(currentUserRole);

  const [fetchedApplicationNotes, setFetchedApplicationNotes] = useState<
    unknown[] | null
  >(null);
  const [isEditingVerifiedIncome, setIsEditingVerifiedIncome] = useState(false);
  const [verifiedIncomeValue, setVerifiedIncomeValue] = useState("");
  const [savingVerifiedIncome, setSavingVerifiedIncome] = useState(false);

  const loadApplicationNotes = useCallback(async (): Promise<unknown[] | null> => {
    if (applicationId == null || applicationId === "") return null;
    try {
      return await getApplicationNotes(applicationId);
    } catch {
      return [];
    }
  }, [applicationId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rows = await loadApplicationNotes();
      if (!cancelled) {
        setFetchedApplicationNotes(rows);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadApplicationNotes]);

  const underwriterVerifiedIncome = resolveIncomeTabVerifiedIncome(
    applicationData as { underwriter_verified_income?: unknown },
  );

  const showVerifiedIncomeSummary = showVerifiedIncomeSummaryHelper(
    applicationData as {
      underwriter_verified_income?: unknown;
      is_approved_require?: unknown;
    },
  );

  const verifiedIncomeMeta = useMemo(() => {
    const verifiedIncomeRecord = applicationData;
    const getVerifiedIncomeMetaValue = (keys: string[]) => {
      for (const key of keys) {
        const value = verifiedIncomeRecord[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          return String(value);
        }
      }
      return "";
    };

    const applicationNotes = (
      fetchedApplicationNotes ??
      (Array.isArray(embeddedNotes) ? embeddedNotes : [])
    ) as Array<Record<string, unknown>>;

    const getNoteBody = (note: Record<string, unknown>) =>
      String(note.notes ?? note.note ?? note.note_text ?? "");

    const isVerifiedIncomeNote = (note: Record<string, unknown>) => {
      const text = getNoteBody(note)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
      return (
        text.startsWith("underwriting verified income") ||
        text.includes(" verified income")
      );
    };

    const getNoteTimestamp = (note: Record<string, unknown>) => {
      const raw = note.updated_at ?? note.created_at;
      if (raw == null || raw === "") return 0;
      if (typeof raw === "number") return raw > 1e12 ? raw : raw * 1000;
      const value = String(raw).trim();
      if (/^\d{10,13}$/.test(value)) {
        const numeric = Number(value);
        return numeric > 1e12 ? numeric : numeric * 1000;
      }
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const verifiedIncomeNote = applicationNotes
      .filter(isVerifiedIncomeNote)
      .sort((a, b) => getNoteTimestamp(b) - getNoteTimestamp(a))[0];

    const verifiedIncomeNoteAuthor = verifiedIncomeNote
      ? String(
          verifiedIncomeNote.name ??
            verifiedIncomeNote.created_by ??
            verifiedIncomeNote.user_name ??
            "",
        )
      : "";

    const verifiedIncomeModifiedBy =
      verifiedIncomeNoteAuthor ||
      getVerifiedIncomeMetaValue([
        "underwriter_verified_income_modified_by",
        "underwriter_verified_income_updated_by",
        "verified_income_modified_by",
        "verified_income_updated_by",
        "modified_by",
      ]);

    const verifiedIncomeLastVerifiedRaw =
      verifiedIncomeNote?.updated_at ??
      verifiedIncomeNote?.created_at ??
      getVerifiedIncomeMetaValue([
        "underwriter_verified_income_modified_at",
        "underwriter_verified_income_updated_at",
        "verified_income_modified_at",
        "verified_income_updated_at",
        "updated_at",
      ]);

    const verifiedIncomeLastVerified = verifiedIncomeLastVerifiedRaw
      ? formatDateTime(verifiedIncomeLastVerifiedRaw as string | number)
      : "";

    return { verifiedIncomeModifiedBy, verifiedIncomeLastVerified };
  }, [applicationData, embeddedNotes, fetchedApplicationNotes]);

  const handleSaveVerifiedIncome = async () => {
    const numeric = parseFloat(`${verifiedIncomeValue}`.replace(/[^0-9.]+/g, ""));
    if (!Number.isFinite(numeric) || numeric < 0) {
      showError("Please enter a valid income value.");
      return;
    }
    const incomePayload = numeric.toFixed(2);
    setSavingVerifiedIncome(true);
    try {
      await saveApplicantVerifiedIncome(applicantId, {
        underwriter_verified_income: incomePayload,
      });
      if (onRefresh) {
        await onRefresh();
      }
      const updatedNotes = await loadApplicationNotes();
      setFetchedApplicationNotes(updatedNotes);
      showSuccess("Verified income Updated Successfully");
      setIsEditingVerifiedIncome(false);
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to update verified income",
      );
    } finally {
      setSavingVerifiedIncome(false);
    }
  };

  return {
    canEditVerifiedIncome,
    showVerifiedIncomeSummary,
    underwriterVerifiedIncome,
    verifiedIncomeModifiedBy: verifiedIncomeMeta.verifiedIncomeModifiedBy,
    verifiedIncomeLastVerified: verifiedIncomeMeta.verifiedIncomeLastVerified,
    isEditingVerifiedIncome,
    setIsEditingVerifiedIncome,
    verifiedIncomeValue,
    setVerifiedIncomeValue,
    savingVerifiedIncome,
    handleSaveVerifiedIncome,
  };
}
